import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { Formik } from 'formik';
import { timingKeyValuePair } from 'screens/helpers/constants';
import useProductsData from 'hooks/useProductData';
import {
  Modal,
  Select,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import ConvertPlanTable from './ConvertPlanTable';
import { computeProductVisibility } from './helpers';
import './style.css';
import {
  ConvertPlanModalV2Props,
  PlanProductEntry,
  DropdownOption
} from './types';

const baseDropDownValues: DropdownOption[] = [{ key: 'none', value: 'None' }];

const initialValues = {
  timing: '',
  timingTag: ''
};

const ConvertPlanModalV2: React.FC<ConvertPlanModalV2Props> = ({
  open,
  currentPlan,
  title,
  description,
  conversionType,
  onSubmit,
  onClose,

  confirmButtonText = 'Next',
  confirmDisabled = false,
  cancelButtonText = 'Cancel'
}) => {
  const [mappedProducts, setMappedProducts] = useState<PlanProductEntry[]>([]);
  const { loading } = useProductsData();
  const [timings, setTimings] = useState<DropdownOption[]>(timingKeyValuePair);
  const [timingTags, setTimingTags] = useState<DropdownOption[]>([]);
  const [timingDropValue, setTimingDropValue] = useState<string | null>(null);
  const [timingTagDropValue, setTimingTagDropValue] = useState<string | null>(
    null
  );
  const [formSessionKey, setFormSessionKey] = useState(0);

  // Matches Applications/Work Orders' own Timing Event field
  // (DetailsSection/index.tsx's `useDropdownState`): a chevron that flips
  // between up/down while open, plus the same `custom-select` styling and
  // `getPopupContainer` behavior.
  const [isTimingOpen, setIsTimingOpen] = useState(false);
  const [isTimingTagOpen, setIsTimingTagOpen] = useState(false);
  const chevronIcon = (isOpen: boolean) =>
    isOpen ? (
      <ChevronUp width={18} height={18} />
    ) : (
      <ChevronDown width={18} height={18} />
    );

  const resetMappedProducts = () => {
    const resetMapped = mappedProducts.map(prod => ({
      ...prod,
      hide: false,
      marked: false
    }));
    setMappedProducts(resetMapped);
  };

  const closeModal = () => {
    setTimingDropValue(null);
    setTimingTagDropValue(null);
    resetMappedProducts();
    onClose();
  };

  const handleSubmit = () => {
    const selectedProducts = mappedProducts.filter(p => p.marked);
    onSubmit(selectedProducts, conversionType);
  };

  const markUnmarkAll = (isMarked: boolean) => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prod.hide ? prod.marked : isMarked
    }));

    setMappedProducts(updatedProducts);
  };

  const markUnmarkProduct = (isMarked: boolean, prodIdx: string) => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prodIdx === prod.trackingId ? isMarked : prod.marked
    }));

    setMappedProducts(updatedProducts);
  };

  const applyProductVisibility = (
    timingKey: string | null,
    timingTagKey: string | null
  ) => {
    setMappedProducts(prev =>
      computeProductVisibility(prev, timingKey, timingTagKey)
    );
  };

  useEffect(() => {
    if (!currentPlan?.planProductEntries) {
      setMappedProducts([]);
      return;
    }

    const planProducts = currentPlan.planProductEntries.map(planProd => ({
      marked: false,
      hide: false,
      ...planProd
    }));
    setMappedProducts(planProducts);

    const newTimings = cloneDeep(baseDropDownValues);
    const combined = newTimings.concat(cloneDeep(timingKeyValuePair));
    setTimings(combined);

    const tempTimingTags = cloneDeep(baseDropDownValues);
    currentPlan.planProductEntries.forEach(prod => {
      if (
        prod.timingEventTag &&
        !tempTimingTags.some(tag => tag.key === prod.timingEventTag)
      ) {
        tempTimingTags.push({
          key: prod.timingEventTag,
          value: prod.timingEventTag
        });
      }
    });
    setTimingTags(tempTimingTags);
  }, [currentPlan?.planProductEntries]);

  // Set modal state to initial values when modal is opened
  useEffect(() => {
    if (open) {
      setTimingDropValue(null);
      setTimingTagDropValue(null);
      setFormSessionKey(k => k + 1);
    }
  }, [open]);

  return (
    <Modal
      className="convert-plan-modal"
      open={open}
      onCancel={closeModal}
      closable={false}
      width="100%"
      footer={null}
      // antd's default zoom transition computes its transform-origin from
      // the trigger element's position, not the modal's own center, so it
      // visibly flies in from an arbitrary spot. Disabling it (empty
      // motion name) makes it appear/disappear in place with no animation.
      transitionName=""
      maskTransitionName=""
    >
      <div className="flex flex-col convert-plan-modal-content">
        <div className="text-xl font-bold">{title}</div>
        <Formik
          key={formSessionKey}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: formikHandleSubmit }) => {
            return (
              <>
                <div className="flex w-full my-4">
                  <div className="w-full mr-4">
                    <label htmlFor="timing" className="block mb-1">
                      Timing
                    </label>
                    <Select
                      id="timing"
                      data-testid="timing-select"
                      style={{ width: '100%' }}
                      placeholder="Select"
                      showSearch
                      allowClear={!!timingDropValue}
                      optionFilterProp="label"
                      className="custom-select"
                      showArrow
                      suffixIcon={chevronIcon(isTimingOpen)}
                      onDropdownVisibleChange={setIsTimingOpen}
                      getPopupContainer={getDropdownParentWithoutDetaching}
                      value={timingDropValue ?? undefined}
                      options={timings.map(item => ({
                        label: item.value,
                        value: item.key
                      }))}
                      onChange={(selectedKey?: string) => {
                        if (!selectedKey) {
                          applyProductVisibility(null, timingTagDropValue);
                          setTimingDropValue(null);
                          return;
                        }
                        const parsed = timings.find(
                          item => item.key === selectedKey
                        ) as DropdownOption;
                        applyProductVisibility(parsed.key, timingTagDropValue);
                        setTimingDropValue(parsed.key);
                      }}
                    />
                  </div>
                  <div className="w-full">
                    <label htmlFor="timingTag" className="block mb-1">
                      Timing Tag
                    </label>
                    <Select
                      id="timingTag"
                      data-testid="timingtag-select"
                      style={{ width: '100%' }}
                      placeholder="Select"
                      showSearch
                      allowClear={!!timingTagDropValue}
                      optionFilterProp="label"
                      className="custom-select"
                      showArrow
                      suffixIcon={chevronIcon(isTimingTagOpen)}
                      onDropdownVisibleChange={setIsTimingTagOpen}
                      getPopupContainer={getDropdownParentWithoutDetaching}
                      value={timingTagDropValue ?? undefined}
                      options={timingTags.map(item => ({
                        label: item.value,
                        value: item.key
                      }))}
                      onChange={(selectedKey?: string) => {
                        if (!selectedKey) {
                          applyProductVisibility(timingDropValue, null);
                          setTimingTagDropValue(null);
                          return;
                        }
                        const parsed = timingTags.find(
                          item => item.key === selectedKey
                        ) as DropdownOption;
                        applyProductVisibility(timingDropValue, parsed.key);
                        setTimingTagDropValue(parsed.key);
                      }}
                    />
                  </div>
                </div>
                <div className="text-xl font-bold">Products & Services</div>
                {description && (
                  <div className="text-xs mb-4">{description}</div>
                )}
                <div
                  className="mb-8 border border-solid border-neutral-20 rounded overflow-y-auto w-full"
                  style={{ flex: 1 }}
                >
                  <ConvertPlanTable
                    planProducts={mappedProducts}
                    markAll={markUnmarkAll}
                    markUnmarkProduct={markUnmarkProduct}
                    loading={loading}
                  />
                </div>
                <div className="flex justify-end">
                  <div className="mr-4">
                    <Button type={ButtonType.outline} onClick={closeModal}>
                      {cancelButtonText}
                    </Button>
                  </div>
                  <Button
                    type={ButtonType.primary}
                    onClick={formikHandleSubmit}
                    disabled={confirmDisabled}
                  >
                    {confirmButtonText}
                  </Button>
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

export default ConvertPlanModalV2;
